import { useMemo } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';

import { SortingOption } from '@sorare/core/src/__generated__/globalTypes';
import { RadioGroup } from '@sorare/core/src/atoms/inputs/RadioGroup';
import { Accordion } from '@sorare/core/src/atoms/layout/Accordion';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useQueryState } from '@sorare/core/src/hooks/useQueryState';
import { filters } from '@sorare/core/src/lib/glossary';
import { toCamelCase } from '@sorare/core/src/lib/toCamelCase';

import { DefaultSortType } from './types.ts';

const messages = defineMessages({
  latest: {
    defaultMessage: 'Latest',
  },
  oldest: {
    defaultMessage: 'Oldest',
  },
});

export const useSortByDate = () => {
  const { formatMessage } = useIntlContext();

  const [{ sortType }, setSortType] = useQueryState({
    sortType: DefaultSortType.LATEST,
  });

  const onSortTypeChange = (value: DefaultSortType) => {
    setSortType({ sortType: value });
  };

  const sortAsVariable =
    sortType === DefaultSortType.LATEST
      ? SortingOption.DESC
      : SortingOption.ASC;

  const sortOptions = useMemo(
    () =>
      Object.values(DefaultSortType).map(element => ({
        label: formatMessage(
          messages[toCamelCase(element) as keyof typeof messages]
        ),
        value: element,
      })),
    [formatMessage]
  );

  return {
    sortOptions,
    sortType,
    sortAsVariable,
    sortSelectNode: (
      <Accordion
        title={
          <LabelM bold>
            <FormattedMessage {...filters.sortBy} />
          </LabelM>
        }
        startsOpen
      >
        <RadioGroup
          name="sortType"
          options={sortOptions}
          value={sortType}
          onChange={onSortTypeChange}
        />
      </Accordion>
    ),
  };
};
